import React, { useEffect, useMemo, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { useFamilyData } from "../../hooks/useFamilyData";
import { getLocalDateStr } from "../../utils/time";
import { useToast } from "../../components/ui/ToastProvider";
import { mapFirebaseError } from "../../utils/userErrors";

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toMs = (value) => {
  const date = toDate(value);
  return date ? date.getTime() : 0;
};

export default function GoalsCard({ familyId, familySettings, canManage }) {
  const { showToast } = useToast();
  const { kids = [], history = [] } = useFamilyData(familyId);
  const [goalName, setGoalName] = useState(familySettings?.familyGoalName || "");
  const [goalPoints, setGoalPoints] = useState(familySettings?.familyGoalPoints || 0);
  const [challengeName, setChallengeName] = useState(familySettings?.challengeName || "");
  const [challengeTarget, setChallengeTarget] = useState(familySettings?.challengeTarget || 50);
  const [savingGoal, setSavingGoal] = useState(false);
  const [savingChallenge, setSavingChallenge] = useState(false);
  const [endingChallenge, setEndingChallenge] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [savedText, setSavedText] = useState("");

  useEffect(() => {
    setGoalName(familySettings?.familyGoalName || "");
    setGoalPoints(familySettings?.familyGoalPoints || 0);
    setChallengeName(familySettings?.challengeName || "");
    setChallengeTarget(familySettings?.challengeTarget || 50);
  }, [
    familySettings?.familyGoalName,
    familySettings?.familyGoalPoints,
    familySettings?.challengeName,
    familySettings?.challengeTarget
  ]);

  const totalPoints = useMemo(
    () => kids.reduce((sum, k) => sum + (k.points || 0), 0),
    [kids]
  );
  const goalProgress = Number(goalPoints) > 0 ? Math.min((totalPoints / Number(goalPoints)) * 100, 100) : 0;

  const challengeProgress = useMemo(() => {
    const startMs = toMs(familySettings?.challengeStartAt);
    if (!startMs) return 0;
    const approved = history.filter((entry) => entry.type === "approve" && toMs(entry.createdAt) >= startMs);
    const points = approved.reduce((sum, h) => sum + (h.pointsAwarded || h.points || 0), 0);
    return points;
  }, [familySettings?.challengeStartAt, history]);

  const saveFamilyGoal = async () => {
    if (!canManage) return;
    if (!goalName.trim()) {
      setErrorText("Goal name is required.");
      return;
    }
    if (Number(goalPoints) <= 0) {
      setErrorText("Goal points must be greater than 0.");
      return;
    }
    setErrorText("");
    setSavedText("");
    setSavingGoal(true);
    try {
      await updateDoc(doc(db, "families", familyId), {
        familyGoalName: goalName.trim(),
        familyGoalPoints: Number(goalPoints) || 0
      });
      setSavedText("Family goal saved.");
      showToast({ type: "success", message: "Family goal saved." });
    } catch (error) {
      setErrorText(mapFirebaseError(error, "Unable to save family goal."));
    } finally {
      setSavingGoal(false);
    }
  };

  const startChallenge = async () => {
    if (!canManage) return;
    if (!challengeName.trim()) {
      setErrorText("Challenge name is required.");
      return;
    }
    if (Number(challengeTarget) <= 0) {
      setErrorText("Challenge target must be greater than 0.");
      return;
    }
    setErrorText("");
    setSavedText("");
    setSavingChallenge(true);
    try {
      await updateDoc(doc(db, "families", familyId), {
        challengeName: challengeName.trim(),
        challengeTarget: Number(challengeTarget) || 0,
        challengeStartAt: new Date(),
        challengeActive: true
      });
      setSavedText("Challenge started.");
      showToast({ type: "success", message: "Weekly challenge started." });
    } catch (error) {
      setErrorText(mapFirebaseError(error, "Unable to start challenge."));
    } finally {
      setSavingChallenge(false);
    }
  };

  const endChallenge = async () => {
    if (!canManage) return;
    setErrorText("");
    setSavedText("");
    setEndingChallenge(true);
    try {
      await updateDoc(doc(db, "families", familyId), {
        challengeActive: false
      });
      setSavedText("Challenge ended.");
      showToast({ message: "Challenge ended." });
    } catch (error) {
      setErrorText(mapFirebaseError(error, "Unable to end challenge."));
    } finally {
      setEndingChallenge(false);
    }
  };

  return (
    <div className="glass parent-card">
      <div className="header-flex">
        <h2>Family Goals & Challenges</h2>
      </div>
      <div className="card-body">
        {!canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        )}
        {canManage && (
          <>
            {errorText && (
              <small className="pin-error" role="alert">
                {errorText}
              </small>
            )}
            {!errorText && savedText && (
              <small className="success-message" role="status" style={{ textAlign: "left" }}>
                {savedText}
              </small>
            )}
            <div className="setting-section">
              <h3>Family Goal</h3>
              <input
                placeholder="Goal name (e.g. Movie Night)"
                value={goalName}
                onChange={(e) => setGoalName(e.target.value)}
              />
              <input
                type="number"
                placeholder="Points target"
                value={goalPoints}
                onChange={(e) => setGoalPoints(e.target.value)}
              />
              <button className="btn-nav success-btn" onClick={saveFamilyGoal} disabled={savingGoal}>
                {savingGoal ? "Saving..." : "Save Goal"}
              </button>
              {familySettings?.familyGoalPoints > 0 && (
                <div className="progress-bg" style={{ marginTop: "8px" }}>
                  <div className="progress-fill" style={{ width: `${goalProgress}%` }} />
                </div>
              )}
              {familySettings?.familyGoalPoints > 0 && (
                <small style={{ opacity: 0.7 }}>
                  {totalPoints}/{familySettings.familyGoalPoints} pts
                </small>
              )}
            </div>

            <div className="setting-section">
              <h3>Weekly Challenge</h3>
              <input
                placeholder="Challenge name (e.g. 50 Points Week)"
                value={challengeName}
                onChange={(e) => setChallengeName(e.target.value)}
              />
              <input
                type="number"
                placeholder="Target points"
                value={challengeTarget}
                onChange={(e) => setChallengeTarget(e.target.value)}
              />
              <div style={{ display: "flex", gap: "8px" }}>
                <button className="btn-nav success-btn" onClick={startChallenge} disabled={savingChallenge}>
                  {savingChallenge ? "Starting..." : "Start Challenge"}
                </button>
                {familySettings?.challengeActive && (
                  <button className="btn-nav" onClick={endChallenge} disabled={endingChallenge}>
                    {endingChallenge ? "Ending..." : "End Challenge"}
                  </button>
                )}
              </div>
              {familySettings?.challengeActive && (
                <div style={{ marginTop: "8px" }}>
                  <div className="progress-bg">
                    <div
                      className="progress-fill"
                      style={{
                        width: `${Math.min((challengeProgress / (familySettings?.challengeTarget || 1)) * 100, 100)}%`
                      }}
                    />
                  </div>
                  <small style={{ opacity: 0.7 }}>
                    Started {getLocalDateStr(toDate(familySettings.challengeStartAt) || new Date())} • {challengeProgress}/{familySettings.challengeTarget} pts
                  </small>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
